import React, { useRef, useEffect } from 'react';

import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { Transformer, Rect } from 'react-konva';

import type { ResizeableRectangleProps } from '@vlabs/monokit/shared/ui/regions-drawing/types';

import { CURSORS } from './constants';
import { setStageCursor } from './setStageCursor';
import { CANVAS_COLORS, DEFAULT_COLOR } from '../regions-drawing/colors';
import {
  ACTIVE_STROKE_WIDTH, ANCHOR_SIZE, STROKE_WIDTH, EPSILON,
} from '../regions-drawing/constants';

const tlCorner = ({ x, y }) => ({ x, y });
const brCorner = ({
  x, y, width, height,
}) => ({ x: x + width, y: y + height });

/** For a full list of props and methods, see:
 - Konva.Transformer docs https://konvajs.org/api/Konva.Transformer.html
 - Konva.Rect docs https://konvajs.org/api/Konva.Rect.html
 */
export function ResizeableRectangle({
  rect,
  isSelected = false,
  enabledAnchors = [
    'top-left',
    'top-center',
    'top-right',
    'middle-right',
    'middle-left',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ],
  transformerProps = {},
  bbox = undefined,
  onUpdate = undefined,
  onSelect = undefined,
  ...props
}: ResizeableRectangleProps): React.JSX.Element {
  const transformerRef = useRef<Konva.Transformer | null>(null);
  const rectRef = useRef<Konva.Rect | null>(null);

  const stage = rectRef.current?.getStage();
  const stageSize = {
    width: stage?.width() ?? 0,
    height: stage?.height() ?? 0,
    offsetX: stage?.x() ?? 0,
    offsetY: stage?.y() ?? 0,
  };

  // Верхний левый / правый нижний углы в абсолютных координатах
  const absMinX = (bbox ? tlCorner(bbox).x : 0);
  const absMinY = (bbox ? tlCorner(bbox).y : 0);
  const absMaxX = (bbox ? brCorner(bbox).x : stageSize.width);
  const absMaxY = (bbox ? brCorner(bbox).y : stageSize.height);

  // Верхний левый / правый нижний углы в относительных координатах
  const relMinX = absMinX - stageSize.offsetX;
  const relMinY = absMinY - stageSize.offsetY;
  const relMaxX = absMaxX - stageSize.offsetX;
  const relMaxY = absMaxY - stageSize.offsetY;

  const onRectClick = (e: Konva.KonvaEventObject<MouseEvent>): void => {
    e.cancelBubble = true;
    setStageCursor(e, CURSORS.move);
    onSelect?.();
  };

  const onDragMove = (e: KonvaEventObject<DragEvent>): void => {
    const node = e.target;

    if (node.x() < relMinX) node.x(relMinX);
    if ((node.x() + node.width()) >= relMaxX) node.x(relMaxX - node.width());
    if (node.y() < relMinY) node.y(relMinY);
    if ((node.y() + node.height()) >= relMaxY) node.y(relMaxY - node.height());
  };

  const onDragEnd = (e: KonvaEventObject<DragEvent>): void => {
    const node = e.target;

    onUpdate?.({
      ...rect,
      width: Math.round(node.width()),
      height: Math.round(node.height()),
      x: Math.round(node.x()),
      y: Math.round(node.y()),
    });
  };

  const onMouseEnter = (e: KonvaEventObject<MouseEvent>): void => {
    setStageCursor(e, isSelected ? CURSORS.move : CURSORS.pointer);

    rectRef.current?.strokeWidth(ACTIVE_STROKE_WIDTH);
    rectRef.current?.fill(CANVAS_COLORS[rect?.color]?.hovered);
  };

  const onMouseLeave = (): void => {
    rectRef.current?.strokeWidth(STROKE_WIDTH);
    rectRef.current?.fill(CANVAS_COLORS[rect?.color]?.unhovered);
  };

  const onTransformEnd = (e: KonvaEventObject<MouseEvent>): void => {
    const node = e.target;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Reset the scale
    node.scaleX(1);
    node.scaleY(1);

    // Update the width and height
    node.width(node.width() * scaleX);
    node.height(node.height() * scaleY);

    e.target.getStage()!.batchDraw();

    onUpdate?.({
      ...rect,
      width: Math.round(node.width()),
      height: Math.round(node.height()),
      x: Math.round(node.x()),
      y: Math.round(node.y()),
    });
  };

  const onBoundBoxTransform = (absOldBox, absNewBox) => {
    const result = { ...absNewBox };

    if ((absMinX - tlCorner(absNewBox).x) > EPSILON) {
      result.x = absMinX;
      result.width = brCorner(absOldBox).x - result.x;
    }
    if ((absMinY - tlCorner(absNewBox).y) > EPSILON) {
      result.y = absMinY;
      result.height = brCorner(absOldBox).y - result.y;
    }
    if ((absMaxX - brCorner(absNewBox).x) < EPSILON) {
      result.width = absMaxX - tlCorner(absOldBox).x;
    }
    if (((absMaxY) - brCorner(absNewBox).y) < EPSILON) {
      result.height = absMaxY - tlCorner(absOldBox).y;
    }
    result.x = Math.max(absMinX, result.x);
    result.y = Math.max(absMinY, result.y);
    result.width = Math.min(absMaxX, brCorner(result).x) - result.x;
    result.height = Math.min(absMaxY, brCorner(result).y) - result.y;

    return result;
  };

  useEffect(() => {
    if (!rectRef.current) return;

    if (isSelected) {
      // It's needed to attach transformer manually
      transformerRef.current?.nodes?.([rectRef.current]);
    }
  }, [isSelected, rectRef]);

  return (
    <React.Fragment>
      <Rect
        fill={CANVAS_COLORS[rect?.color]?.unhovered}
        height={rect?.height}
        onClick={onRectClick}
        onDragEnd={onDragEnd}
        onDragMove={onDragMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTransformEnd={onTransformEnd}
        ref={rectRef}
        stroke={CANVAS_COLORS[rect?.color]?.base ?? DEFAULT_COLOR}
        strokeWidth={STROKE_WIDTH}
        width={rect?.width}
        x={rect?.x}
        y={rect?.y}
        {...props}
      />

      {!!isSelected && (
        <Transformer
          {...transformerProps}
          anchorSize={ANCHOR_SIZE}
          anchorStroke={CANVAS_COLORS[rect?.color]?.base ?? DEFAULT_COLOR}
          anchorStrokeWidth={STROKE_WIDTH}
          boundBoxFunc={onBoundBoxTransform}
          enabledAnchors={enabledAnchors}
          flipEnabled={false}
          ref={transformerRef}
        />
      )}
    </React.Fragment>
  );
}
